import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Star, Check, X, Plus, Grid, List } from 'lucide-react';
import styled from 'styled-components';
import productService from '../services/productService';
import { useComparison } from '../hooks/useComparison.jsx';

// Fonction utilitaire pour les images par défaut
const getDefaultImage = (product) => {
  if (product.image) {
    return `https://inza-backend.sajholding.org${product.image}`;
  }
  return '/api/placeholder/300/300';
};

// Styled Components
const Section = styled.section`
  padding: 2rem 1rem;
  background: #f9fafb;
  
  @media (min-width: 768px) {
    padding: 2rem 2rem;
  }
`;

const Container = styled.div`
  max-width: 120rem;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: clamp(0.9rem, 2vw, 1.1rem);
    color: #6b7280;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ProductSelector = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  }
`;

const SelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  
  h3 {
    font-size: 1.4rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
  
  .count {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 25px;
    font-size: 0.9rem;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
  }
`;

const BrowseButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.5rem auto 0;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    width: 100%;
    justify-content: center;
  }
`;

const ProductCardSelector = styled.div`
  background: white;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 12px;
  padding: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: #3b82f6;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
  }
  
  ${props => props.selected && `
    border-color: #3b82f6;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
  `}
  
  .product-image {
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 1.2rem;
    transition: transform 0.3s ease;
    border: 2px solid #f3f4f6;
  }
  
  &:hover .product-image {
    transform: scale(1.05);
    border-color: #3b82f6;
  }
  
  .product-content {
    text-align: center;
  }
  
  .product-name {
    font-weight: 700;
    color: #1f2937;
    font-size: 0.95rem;
    margin-bottom: 0.8rem;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .product-price {
    color: #3b82f6;
    font-weight: 800;
    font-size: 1.1rem;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    padding: 0.5rem 1rem;
    border-radius: 8px;
    display: inline-block;
    margin-top: 0.5rem;
  }
  
  .checkbox {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 28px;
    height: 28px;
    background: ${props => props.selected ? '#3b82f6' : 'white'};
    border: 3px solid #3b82f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    transition: all 0.2s ease;
  }
`;

const ComparisonTable = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  background: #1f2937;
  color: white;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
  }
  
  .actions {
    display: flex;
    gap: 1rem;
  }
`;

const ClearButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
  
  &:hover {
    background: #dc2626;
  }
`;

const AddMoreButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #059669;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ComparisonContent = styled.div`
  overflow-x: auto;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const ProductCard = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
    transform: translateY(-4px);
  }
`;

const ProductHeader = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 1.5rem;
  text-align: center;
  position: relative;
  
  .remove-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  
  .product-image {
    width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 8px;
    margin: 0 auto 1rem;
    border: 3px solid rgba(255, 255, 255, 0.3);
  }
  
  .product-name {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }
  
  .product-price {
    font-size: 1.3rem;
    font-weight: 800;
    color: #fbbf24;
  }
`;

const FeatureList = styled.div`
  padding: 1.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  .feature-label {
    font-weight: 600;
    color: #6b7280;
    font-size: 0.9rem;
  }
  
  .feature-value {
    font-weight: 700;
    color: #1f2937;
    text-align: right;
    
    &.highlight {
      color: #3b82f6;
      font-size: 1.05rem;
    }
    
    &.stock-available {
      color: #10b981;
    }
    
    &.stock-out {
      color: #ef4444;
    }
    
    &.na {
      color: #9ca3af;
      font-style: italic;
      font-weight: 400;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #374151;
  }
  
  p {
    margin-bottom: 2rem;
  }
`;

const ComparisonSectionEnhanced = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);
  const navigate = useNavigate();
  
  const {
    selectedProducts,
    maxProducts,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isSelected,
    getCount,
    canAddMore,
    getRemainingSlots
  } = useComparison();

  // Charger tous les produits disponibles
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const products = await productService.getProducts();
        setAllProducts(products || []);
      } catch (error) {
        console.error('Error loading products:', error);
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Fonction pour comparer les caractéristiques
  const getComparisonFeatures = () => {
    const features = [
      { key: 'name', label: 'Nom' },
      { key: 'price', label: 'Prix' },
      { key: 'storage', label: 'Stockage' },
      { key: 'color', label: 'Couleur' },
      { key: 'condition', label: 'État' },
      { key: 'category_name', label: 'Catégorie' },
      { key: 'stock', label: 'Stock' },
    ];

    return features;
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return parseInt(price).toLocaleString('fr-FR') + ' FCFA';
  };

  if (loading) {
    return (
      <Section>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div>Chargement des produits...</div>
          </div>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <Header>
          <h2>Comparaison de Produits</h2>
          <p>
            {getCount() > 0 
              ? `${getCount()} produit${getCount() > 1 ? 's' : ''} sélectionné${getCount() > 1 ? 's' : ''} pour la comparaison`
              : 'Sélectionnez des produits à comparer'
            }
          </p>
        </Header>

        {/* Sélecteur de produits */}
        <ProductSelector>
          <SelectorHeader>
            <h3>Ajouter des produits à comparer</h3>
            <div className="count">
              {getCount()} / {maxProducts}
            </div>
          </SelectorHeader>
          
          <ProductGrid>
            {allProducts.slice(0, 12).map(product => (
              <ProductCard
                key={product.id}
                selected={isSelected(product.id)}
                onClick={() => addToComparison(product)}
                style={{ position: 'relative' }}
              >
                <div className="checkbox" selected={isSelected(product.id)}>
                  {isSelected(product.id) && <Check size={16} />}
                </div>
                <img
                  src={getDefaultImage(product)}
                  alt={product.name}
                  className="product-image"
                />
                <div className="product-content">
                  <div className="product-name">{product.name}</div>
                  <div className="product-price">{formatPrice(product.price)}</div>
                </div>
              </ProductCard>
            ))}
          </ProductGrid>
          
          {/* Bouton pour parcourir tous les produits */}
          <BrowseButton onClick={() => navigate('/boutique')}>
            <Grid size={18} />
            Parcourir les produits
          </BrowseButton>
          
          {!canAddMore() && (
            <div style={{ 
              background: '#fef2f2', 
              color: '#dc2626', 
              padding: '0.75rem', 
              borderRadius: '6px',
              textAlign: 'center',
              marginTop: '1rem'
            }}>
              Maximum de {maxProducts} produits atteint pour la comparaison
            </div>
          )}
        </ProductSelector>

        {/* Tableau de comparaison */}
        <AnimatePresence>
          {selectedProducts.length > 0 && (
            <ComparisonTable
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <TableHeader>
                <h3>Tableau Comparatif ({getCount()} produits)</h3>
                <div className="actions">
                  {canAddMore() && (
                    <AddMoreButton onClick={() => setShowSelector(!showSelector)}>
                      <Plus size={16} />
                      {showSelector ? 'Masquer la sélection' : 'Ajouter des produits'}
                    </AddMoreButton>
                  )}
                  <ClearButton onClick={clearComparison}>
                    Vider tout
                  </ClearButton>
                </div>
              </TableHeader>

              <ComparisonContent>
                <TableGrid>
                  {selectedProducts.map(product => (
                    <ProductCard key={product.id}>
                      <ProductHeader>
                        <button
                          className="remove-btn"
                          onClick={() => removeFromComparison(product.id)}
                          title="Retirer de la comparaison"
                        >
                          <X size={16} />
                        </button>
                        <img
                          src={getDefaultImage(product)}
                          alt={product.name}
                          className="product-image"
                        />
                        <div className="product-name">{product.name}</div>
                        <div className="product-price">{formatPrice(product.price)}</div>
                      </ProductHeader>
                      
                      <FeatureList>
                        <FeatureItem>
                          <span className="feature-label">Nom</span>
                          <span className="feature-value">{product.name || 'N/A'}</span>
                        </FeatureItem>
                        <FeatureItem>
                          <span className="feature-label">Stockage</span>
                          <span className="feature-value">
                            {product.storage || product.storage_capacity || 'N/A'}
                          </span>
                        </FeatureItem>
                        <FeatureItem>
                          <span className="feature-label">Prix</span>
                          <span className="feature-value highlight">{formatPrice(product.price)}</span>
                        </FeatureItem>
                        <FeatureItem>
                          <span className="feature-label">Couleur</span>
                          <span className="feature-value">{product.color || 'N/A'}</span>
                        </FeatureItem>
                        <FeatureItem>
                          <span className="feature-label">État</span>
                          <span className="feature-value">{product.condition || 'N/A'}</span>
                        </FeatureItem>
                        <FeatureItem>
                          <span className="feature-label">Catégorie</span>
                          <span className="feature-value">{product.category_name || 'N/A'}</span>
                        </FeatureItem>
                        <FeatureItem>
                          <span className="feature-label">Stock</span>
                          <span className={`feature-value ${
                            product.stock > 0 ? 'stock-available' : 'stock-out'
                          }`}>
                            {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
                          </span>
                        </FeatureItem>
                      </FeatureList>
                    </ProductCard>
                  ))}
                </TableGrid>
              </ComparisonContent>
            </ComparisonTable>
          )}
        </AnimatePresence>

        {/* État vide */}
        {selectedProducts.length === 0 && (
          <EmptyState>
            <h3>Aucun produit sélectionné</h3>
            <p>Sélectionnez des produits ci-dessus pour commencer la comparaison</p>
          </EmptyState>
        )}
      </Container>
    </Section>
  );
};

export default ComparisonSectionEnhanced;
